import { useEffect, useRef, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Save, X, ChevronDown } from "lucide-react";
import { fmt } from "./PortalUI";

const FIELDS = [["enrollment_specialist", "Enrollment Specialist"], ["program_advisor", "Program Advisor"], ["dispatcher_trainer", "Certified Dispatcher Trainer"], ["workforce_partner", "Workforce Partner"], ["mentor", "Mentor"]];

const trainerInfo = (o) => o.current_load !== undefined
  ? `${o.current_availability} · load ${o.current_load}/${o.max_capacity ?? "∞"} · next ${o.next_available_date || "—"}${o.specialty ? ` · ${o.specialty}` : ""}${o.formats?.length ? ` · ${o.formats.join("/")}` : ` · ${o.training_format || ""}`}`
  : `${o.role}${o.email ? ` · ${o.email}` : ""}`;

export const TeamPicker = ({ field, label, value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const text = value?.name || "";
  const matches = options.filter((o) => !text || `${o.name} ${o.email || ""}`.toLowerCase().includes(text.toLowerCase())).slice(0, 8);
  return (
    <div ref={ref} className="relative">
      <p className="text-xs text-dark-text/50 mb-1">{label}</p>
      <div className="flex items-center border border-navy/15 rounded-md focus-within:ring-2 focus-within:ring-magenta bg-white">
        <input value={text} data-testid={`assign-${field}`} autoComplete="off" placeholder="Start typing a name…"
          onFocus={() => setOpen(true)} onKeyDown={(e) => { if (e.key === "Escape" || e.key === "Tab") setOpen(false); }}
          onChange={(e) => { onChange({ name: e.target.value, email: "", role: "" }); setOpen(true); }}
          className="flex-1 min-w-0 px-3 py-2 text-sm outline-none rounded-md bg-transparent" />
        {text && <button type="button" tabIndex={-1} onClick={() => onChange(null)} className="px-2 text-dark-text/40 hover:text-red-600" data-testid={`assign-clear-${field}`}><X size={14} /></button>}
        <button type="button" tabIndex={-1} onClick={() => setOpen(!open)} className="px-2 text-dark-text/40"><ChevronDown size={14} /></button>
      </div>
      {value?.email && <p className="text-[11px] text-dark-text/50 mt-0.5" data-testid={`assign-meta-${field}`}>{value.role || ""} · {value.email}</p>}
      {open && matches.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full bg-white border border-navy/15 rounded-md shadow-lg max-h-64 overflow-y-auto" data-testid={`assign-options-${field}`}>
          {matches.map((o) => (
            <li key={o.email || o.name}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange({ name: o.name, email: o.email || "", role: o.role }); setOpen(false); }}
                className="w-full text-left px-3 py-2 hover:bg-navy-light" data-testid={`assign-option-${field}-${(o.email || o.name).replace(/[^a-z0-9]/gi, "")}`}>
                <span className="block text-sm font-semibold text-navy">{o.name}{o.fully_booked && <span className="ml-2 text-[10px] text-red-600 font-bold">FULL</span>}</span>
                <span className="block text-[11px] text-dark-text/50">{trainerInfo(o)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const initial = (d) => Object.fromEntries(FIELDS.map(([k]) => [k, (d.team || {})[k] || (d.assignments?.[k] ? { name: d.assignments[k], email: "", role: "" } : null)]));

export const TeamAssign = ({ d, reload }) => {
  const [team, setTeam] = useState(() => initial(d));
  const [opts, setOpts] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/team-options").then((r) => setOpts(r.data)).catch(() => {}); }, []);
  useEffect(() => { setTeam(initial(d)); }, [d]);

  const save = async (override = false) => {
    setBusy(true);
    try {
      const members = Object.fromEntries(FIELDS.map(([k]) => [k, team[k]?.name ? { name: team[k].name, email: team[k].email || null, role: team[k].role || null } : null]));
      await api.put(`/admin/applicants/${d.applicant_id}/team`, { members, override });
      toast.success("Assignments saved"); reload();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.code === "trainer_warning") {
        if (window.confirm(`Trainer warning:\n\n${det.warnings.join("\n")}\n\nOverride and assign anyway?`)) { setBusy(false); return save(true); }
      } else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };
  const hist = (d.team_history || []).slice().reverse();
  return (
    <div className="grid grid-cols-1 gap-3" data-testid="team-assign">
      {FIELDS.map(([k, l]) => <TeamPicker key={k} field={k} label={l} value={team[k]} options={opts[k] || []} onChange={(v) => setTeam((t) => ({ ...t, [k]: v }))} />)}
      <button onClick={() => save(false)} disabled={busy} data-testid="save-assignments" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-dark text-white font-bold py-2.5 px-5 rounded-md transition-colors w-fit disabled:opacity-50"><Save size={16} /> {busy ? "Saving…" : "Save Assignments"}</button>
      {FIELDS.some(([k]) => d.team?.[k]) && (
        <div className="text-xs bg-soft-gray rounded-md p-3 space-y-1" data-testid="team-current">
          {FIELDS.filter(([k]) => d.team?.[k]).map(([k, l]) => <p key={k}><strong className="text-navy">{l}:</strong> {d.team[k].name}{d.team[k].email ? ` (${d.team[k].email})` : ""} · {d.team[k].status} · assigned {fmt(d.team[k].assigned_at)} by {d.team[k].assigned_by}</p>)}
        </div>
      )}
      {hist.length > 0 && (
        <details className="text-xs" data-testid="team-history"><summary className="cursor-pointer font-bold text-navy">Assignment history ({hist.length})</summary>
          <ul className="mt-2 space-y-1">{hist.map((h, i) => <li key={i}>{FIELDS.find(([k]) => k === h.field)?.[1]}: {h.name} · {fmt(h.assigned_at)} → {fmt(h.ended_at)} (ended by {h.ended_by})</li>)}</ul></details>
      )}
    </div>
  );
};
